import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { useTick } from "@/context/LiveContext";
import { catLabel } from "@/lib/categories";
import { Panel } from "@/components/DashboardShell";
import { TrendingUp, TrendingDown, Minus, RefreshCw, IndianRupee, Inbox, MessageSquare, UserPlus, Building2, Eye, Mail, Users, Repeat, Timer, Target, Wallet, BadgeCheck, AlertTriangle, CircleCheck, Clock, CircleSlash } from "lucide-react";

/* Chart palette — validated (light surface): adjacent CVD ΔE ≥ 9.1, normal-vision ΔE ≥ 22.9.
   Slots 3–4 sit under 3:1 contrast, so every chart carries a legend + tooltip and values are listed. */
const S = ["#A11E23", "#2a78d6", "#1baf7a", "#eda100", "#6250d6"];
const GRID = "#EFE5D6";
const AXIS = { fontSize: 12, fill: "#8a7576" };
const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [365, "12 months"]];

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDay = (iso, step) => { try { const d = new Date(iso + "T00:00:00"); return d.toLocaleDateString("en-IN", step > 1 ? { day: "numeric", month: "short" } : { day: "numeric", month: "short" }); } catch { return iso; } };

function Delta({ pct }) {
  if (pct == null) return <span className="text-[12px] text-neutral-400 inline-flex items-center gap-1"><Minus className="w-3 h-3"/> no earlier data</span>;
  const up = pct > 0, flat = pct === 0;
  const I = flat ? Minus : up ? TrendingUp : TrendingDown;
  return <span className={`text-[12px] font-semibold inline-flex items-center gap-1 ${flat ? "text-neutral-500" : up ? "text-emerald-700" : "text-rose-700"}`}><I className="w-3.5 h-3.5"/> {up ? "+" : ""}{pct}% vs previous</span>;
}

function Spark({ data, color = S[0] }) {
  const w = 120, h = 32;
  if (!data?.length) return null;
  const max = Math.max(1, ...data);
  const pts = data.map((v, i) => [data.length === 1 ? w : (i / (data.length - 1)) * w, h - 3 - (v / max) * (h - 6)]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mt-3 block" aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity=".08"/>
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

function Kpi({ icon: I, label, value, delta, spark, color }) {
  return (
    <div className="rounded-[18px] bg-white border border-[color:var(--line)] p-4 min-w-0">
      <div className="flex items-center justify-between gap-2"><span className="text-[14px] font-semibold text-[color:var(--ink-2)] truncate">{label}</span><I className="w-4 h-4 text-[color:var(--gold)] shrink-0"/></div>
      <div className={`font-num ${String(value ?? "").length > 10 ? "text-[23px]" : String(value ?? "").length > 7 ? "text-[26px]" : "text-[30px]"} leading-[1.05] text-[color:var(--ink)] mt-3 tabular-nums`} style={{ overflowWrap: "anywhere" }}>{value}</div>
      <div className="mt-1.5"><Delta pct={delta}/></div>
      <Spark data={spark} color={color}/>
    </div>
  );
}

function Mini({ icon: I, label, value, hint }) {
  return (
    <div className="rounded-[16px] bg-[color:var(--cream)] border border-[color:var(--line)] p-4 min-w-0">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-[color:var(--ink-2)]"><I className="w-4 h-4 text-[color:var(--maroon)] shrink-0"/> <span className="truncate">{label}</span></div>
      <div className={`font-num ${String(value ?? "").length > 10 ? "text-[21px]" : "text-[26px]"} leading-[1.05] text-[color:var(--ink)] mt-2.5 tabular-nums`} style={{ overflowWrap: "anywhere" }}>{value}</div>
      {hint && <div className="text-[12px] text-neutral-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function ChartTip({ active, payload, label, money, step }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[color:var(--line)] shadow-lg px-3 py-2 text-[13px]">
      <div className="font-semibold text-[color:var(--ink)] mb-1">{step > 1 ? "Week of " : ""}{fmtDay(label, step)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-[color:var(--ink-2)]">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }}/>{p.name}: <b className="text-[color:var(--ink)]">{money ? inr(p.value) : num(p.value)}</b>
        </div>
      ))}
    </div>
  );
}

function Legend({ items }) {
  return <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">{items.map(([name, color]) => <span key={name} className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--ink-2)]"><span className="w-3 h-[3px] rounded-full" style={{ background: color }}/>{name}</span>)}</div>;
}

function HBar({ label, value, max, color = S[0], suffix = "", sub }) {
  const pct = max ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-[14px]"><span className="text-[color:var(--ink)] truncate">{label}{sub && <span className="text-neutral-400 text-[12px]"> · {sub}</span>}</span><span className="font-semibold text-[color:var(--ink)] tabular-nums">{num(value)}{suffix}</span></div>
      <div className="h-2 rounded-full bg-[color:var(--ivory)] mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value ? pct : 0}%`, background: color }}/></div>
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function Heatmap({ grid }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...grid.flat());
  const total = grid.flat().reduce((a, b) => a + b, 0);
  let peak = [0, 0, 0];
  grid.forEach((r, d) => r.forEach((v, h) => { if (v > peak[2]) peak = [d, h, v]; }));
  const hr = (h) => `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`;
  return (
    <div>
      <div className="text-[13px] text-[color:var(--ink-2)] h-5 mb-2">{hover ? <><b>{DAYS[hover[0]]} {hr(hover[1])}</b> — {hover[2]} request{hover[2] === 1 ? "" : "s"}</> : total ? <>Busiest: <b>{DAYS[peak[0]]} around {hr(peak[1])}</b> ({peak[2]})</> : "No requests in this period yet"}</div>
      <div className="overflow-x-auto">
        <div className="inline-grid gap-[2px]" style={{ gridTemplateColumns: `36px repeat(24, minmax(14px, 1fr))` }} onMouseLeave={() => setHover(null)}>
          <span/>
          {Array.from({ length: 24 }).map((_, h) => <span key={h} className="text-[10px] text-neutral-400 text-center">{h % 3 === 0 ? hr(h) : ""}</span>)}
          {grid.map((row, d) => [
            <span key={`l${d}`} className="text-[12px] text-neutral-500 pr-1 self-center">{DAYS[d]}</span>,
            ...row.map((v, h) => (
              <span key={`${d}-${h}`} onMouseEnter={() => setHover([d, h, v])} className="h-[18px] rounded-[3px] cursor-default"
                style={{ background: v ? `color-mix(in oklab, #8C1C22 ${Math.round(18 + (v / max) * 82)}%, #FBF7F4)` : "#F1EAE2" }} aria-label={`${DAYS[d]} ${hr(h)}: ${v}`}/>
            )),
          ])}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 text-[12px] text-neutral-500">Fewer<span className="w-24 h-2 rounded-full" style={{ background: "linear-gradient(90deg, color-mix(in oklab, #8C1C22 18%, #FBF7F4), #8C1C22)" }}/>More</div>
    </div>
  );
}

export default function Insights() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [funnelAll, setFunnelAll] = useState(false);
  const [cityTab, setCityTab] = useState("demand");
  const tick = useTick();

  const load = () => api.get("/stats/deep", { params: { days } }).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load statistics"));
  useEffect(() => { load(); }, [days, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const series = d?.series || [];
  const step = d?.bucket_days || 1;
  const col = (k) => series.map((x) => x[k] || 0);
  const k = d?.kpis || {};
  const t = d?.totals || {};
  const funnel = d?.funnel || [];
  const fMax = Math.max(1, ...funnel.map((f) => (funnelAll ? f.all : f.period)));
  const cats = d?.categories || [];
  const cMax = Math.max(1, ...cats.map((c) => c.inquiries));
  const exp = d?.expiry || {};
  const expTotal = Object.values(exp).reduce((a, b) => a + b, 0) || 1;
  const cityList = cityTab === "demand" ? d?.top_cities_demand : cityTab === "listings" ? d?.top_cities_listings : d?.top_cities_users;
  const cityMax = Math.max(1, ...(cityList || []).map((c) => c.count));
  const revMax = Math.max(1, ...(d?.revenue_by_plan || []).map((r) => r.amount));
  const tickFmt = useMemo(() => (v) => fmtDay(v, step), [step]);

  if (err) return <div className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-rose-800">{String(err)}</div>;

  return (
    <div className="space-y-6" data-testid="insights">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex p-1 rounded-full bg-white border border-[color:var(--line)]" role="tablist">
          {RANGES.map(([v, l]) => <button key={v} role="tab" aria-selected={days === v} onClick={() => setDays(v)} className={`px-4 py-2 rounded-full text-[14px] font-semibold ${days === v ? "bg-[color:var(--maroon)] text-white" : "text-[color:var(--maroon)]"}`} data-testid={`range-${v}`}>{l}</button>)}
        </div>
        <div className="flex items-center gap-3 text-[13px] text-neutral-500">
          {d && <span>Updated {new Date(d.generated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · refreshes on its own</span>}
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[color:var(--line)] text-[color:var(--maroon)] font-semibold press"><RefreshCw className="w-3.5 h-3.5"/> Refresh</button>
        </div>
      </div>

      {!d ? <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-36 skeleton"/>)}</div> : (<>
        {/* Period KPIs */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Kpi icon={IndianRupee} label="Revenue" value={inr(k.revenue?.current)} delta={k.revenue?.delta_pct} spark={col("revenue")} color={S[0]}/>
          <Kpi icon={MessageSquare} label="New leads" value={num(k.leads?.current)} delta={k.leads?.delta_pct} spark={col("leads")} color={S[1]}/>
          <Kpi icon={Inbox} label="Listing inquiries" value={num(k.inquiries?.current)} delta={k.inquiries?.delta_pct} spark={col("inquiries")} color={S[1]}/>
          <Kpi icon={Eye} label="Visits" value={num(k.visitors?.current)} delta={k.visitors?.previous ? Math.round(((k.visitors.current - k.visitors.previous) * 1000) / k.visitors.previous) / 10 : null} spark={col("visitors")} color={S[0]}/>
          <Kpi icon={UserPlus} label="New customers" value={num(k.new_customers?.current)} delta={k.new_customers?.delta_pct} spark={col("customers")} color={S[1]}/>
          <Kpi icon={Building2} label="New business owners" value={num(k.new_owners?.current)} delta={k.new_owners?.delta_pct} spark={col("owners")} color={S[0]}/>
          <Kpi icon={BadgeCheck} label="New listings" value={num(k.new_listings?.current)} delta={k.new_listings?.delta_pct} spark={col("listings")} color={S[0]}/>
          <Kpi icon={Mail} label="Contact messages" value={num(k.contacts?.current)} delta={k.contacts?.delta_pct} spark={col("contacts")} color={S[1]}/>
        </div>

        {/* Health */}
        <Panel title="Business health">
          <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-3">
            <Mini icon={Wallet} label="Monthly recurring" value={inr(t.mrr)} hint={`${num(t.active_subscriptions)} active plans`}/>
            <Mini icon={IndianRupee} label="Revenue (all time)" value={inr(t.revenue)} hint={`${num(t.paid_subscriptions)} payments`}/>
            <Mini icon={Wallet} label="Collected offline" value={inr(t.offline_collected)} hint={`${inr(t.offline_pending_deposit)} not yet deposited`}/>
            <Mini icon={Users} label="Paying owners" value={num(t.paying_owners)} hint={`${inr(t.arppu)} each on average`}/>
            <Mini icon={Repeat} label="Paid again" value={`${t.repeat_payers_pct ?? 0}%`} hint="of paying owners renewed"/>
            <Mini icon={BadgeCheck} label="Owners with a live listing" value={`${t.owner_activation_pct ?? 0}%`} hint={`${num(t.owners)} owners signed up`}/>
            <Mini icon={Target} label="Leads booked" value={`${t.lead_conversion_pct ?? 0}%`} hint={`${t.lead_loss_pct ?? 0}% marked lost`}/>
            <Mini icon={Timer} label="Time to first contact" value={t.median_hours_to_first_contact == null ? "–" : `${t.median_hours_to_first_contact} h`} hint="median, lead → first follow-up"/>
            <Mini icon={Inbox} label="Inquiries per listing" value={t.inquiries_per_listing ?? 0} hint={`${num(t.live)} live of ${num(t.listings)}`}/>
            <Mini icon={Eye} label="Listing views" value={num(t.views_total)} hint={`${num(t.visitors)} site visits`}/>
            <Mini icon={Mail} label="Newsletter" value={num(t.newsletter)} hint={`+${num(k.newsletter?.current)} this period`}/>
          </div>
        </Panel>

        <div className="grid xl:grid-cols-2 gap-6">
          <Panel title="Demand over time">
            <Legend items={[["Leads", S[1]], ["Listing inquiries", S[0]], ["Contact messages", S[2]]]}/>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false}/>
                  <XAxis dataKey="date" tick={AXIS} tickFormatter={tickFmt} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24}/>
                  <YAxis tick={AXIS} allowDecimals={false} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTip step={step}/>} cursor={{ stroke: "#c9b7b8", strokeWidth: 1 }}/>
                  <Line type="monotone" dataKey="leads" name="Leads" stroke={S[1]} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}/>
                  <Line type="monotone" dataKey="inquiries" name="Listing inquiries" stroke={S[0]} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}/>
                  <Line type="monotone" dataKey="contacts" name="Contact messages" stroke={S[2]} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title="Revenue">
            <div className="text-[13px] text-[color:var(--ink-2)] mb-3">{inr(k.revenue?.current)} in the last {RANGES.find((r) => r[0] === days)?.[1]} · {step > 1 ? "per week" : "per day"}</div>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barCategoryGap={2}>
                  <CartesianGrid stroke={GRID} vertical={false}/>
                  <XAxis dataKey="date" tick={AXIS} tickFormatter={tickFmt} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24}/>
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `₹${Math.round(v / 1000)}k` : `₹${v}`)} width={52}/>
                  <Tooltip content={<ChartTip money step={step}/>} cursor={{ fill: "rgba(140,28,34,.06)" }}/>
                  <Bar dataKey="revenue" name="Revenue" fill={S[0]} radius={[4, 4, 0, 0]} maxBarSize={28}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title="Sign-ups">
            <Legend items={[["Customers", S[1]], ["Business owners", S[0]]]}/>
            <div className="h-60">
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }} barGap={2}>
                  <CartesianGrid stroke={GRID} vertical={false}/>
                  <XAxis dataKey="date" tick={AXIS} tickFormatter={tickFmt} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24}/>
                  <YAxis tick={AXIS} allowDecimals={false} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTip step={step}/>} cursor={{ fill: "rgba(140,28,34,.06)" }}/>
                  <Bar dataKey="customers" name="Customers" fill={S[1]} radius={[4, 4, 0, 0]} maxBarSize={16}/>
                  <Bar dataKey="owners" name="Business owners" fill={S[0]} radius={[4, 4, 0, 0]} maxBarSize={16}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title="Site visits">
            <div className="text-[13px] text-[color:var(--ink-2)] mb-3">{num(k.visitors?.current)} visits this period · {num(t.visitors)} all time</div>
            <div className="h-60">
              <ResponsiveContainer>
                <AreaChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false}/>
                  <XAxis dataKey="date" tick={AXIS} tickFormatter={tickFmt} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24}/>
                  <YAxis tick={AXIS} allowDecimals={false} tickLine={false} axisLine={false}/>
                  <Tooltip content={<ChartTip step={step}/>} cursor={{ stroke: "#c9b7b8", strokeWidth: 1 }}/>
                  <Area type="monotone" dataKey="visitors" name="Visits" stroke={S[0]} strokeWidth={2} fill={S[0]} fillOpacity={0.08} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div className="grid xl:grid-cols-[1.2fr_1fr] gap-6">
          <Panel title="Lead pipeline" action={
            <div className="inline-flex p-0.5 rounded-full bg-[color:var(--cream)] border border-[color:var(--line)] text-[13px]">
              {[[false, "This period"], [true, "All time"]].map(([v, l]) => <button key={l} onClick={() => setFunnelAll(v)} className={`px-3 py-1.5 rounded-full font-semibold ${funnelAll === v ? "bg-[color:var(--maroon)] text-white" : "text-[color:var(--maroon)]"}`}>{l}</button>)}
            </div>}>
            {funnel.map((f) => <HBar key={f.stage} label={f.stage[0].toUpperCase() + f.stage.slice(1)} value={funnelAll ? f.all : f.period} max={fMax} color={f.stage === "lost" ? "#9a8a8b" : f.stage === "booked" ? S[2] : S[1]}/>)}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Mini icon={Target} label="Booked" value={`${t.lead_conversion_pct ?? 0}%`}/>
              <Mini icon={Timer} label="First follow-up" value={t.median_hours_to_first_contact == null ? "–" : `${t.median_hours_to_first_contact} h`}/>
            </div>
          </Panel>
          <Panel title="Where leads come from">
            {(d.lead_sources || []).length ? (d.lead_sources || []).map((s, i) => <HBar key={s.source} label={{ inquiry: "Listing inquiry", contact: "Contact page", manual: "Added by team" }[s.source] || s.source} value={s.count} max={Math.max(...d.lead_sources.map((x) => x.count))} color={S[i % S.length]}/>) : <div className="text-neutral-500 text-[14px] py-6">No leads in this period.</div>}
            <h3 className="font-semibold text-[15px] text-[color:var(--ink)] mt-6 mb-2">Revenue by plan</h3>
            {(d.revenue_by_plan || []).length ? d.revenue_by_plan.map((r) => <div key={r.plan} className="py-1.5"><div className="flex justify-between text-[14px]"><span>{r.label}</span><b>{inr(r.amount)}</b></div><div className="h-2 rounded-full bg-[color:var(--ivory)] mt-1"><div className="h-full rounded-full" style={{ width: `${Math.max(2, (r.amount / revMax) * 100)}%`, background: S[0] }}/></div></div>) : <div className="text-neutral-500 text-[14px]">No payments yet.</div>}
          </Panel>
        </div>

        <Panel title="Categories">
          <div className="overflow-x-auto">
            <table className="w-full text-[14px] min-w-[720px]">
              <thead><tr className="text-left text-neutral-500 border-b border-[color:var(--line)]"><th className="py-2 font-semibold">Category</th><th className="font-semibold">Live</th><th className="font-semibold">Waiting</th><th className="font-semibold">Premium</th><th className="font-semibold">Views</th><th className="font-semibold w-[34%]">Inquiries</th><th className="font-semibold text-right">Per listing</th></tr></thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.key} className="border-b border-[color:var(--line)]/60">
                    <td className="py-2.5 font-semibold text-[color:var(--ink)]">{c.label}</td>
                    <td className="tabular-nums">{c.live}</td>
                    <td className="tabular-nums">{c.pending ? <span className="text-amber-700 font-semibold">{c.pending}</span> : 0}</td>
                    <td className="tabular-nums">{c.premium}</td>
                    <td className="tabular-nums">{num(c.views)}</td>
                    <td><div className="flex items-center gap-2"><div className="flex-1 h-2 rounded-full bg-[color:var(--ivory)]"><div className="h-full rounded-full" style={{ width: `${c.inquiries ? Math.max(3, (c.inquiries / cMax) * 100) : 0}%`, background: S[1] }}/></div><span className="tabular-nums w-10 text-right">{c.inquiries}</span></div></td>
                    <td className="tabular-nums text-right">{c.live ? (c.inquiries / c.live).toFixed(1) : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
          <Panel title="When families reach out"><Heatmap grid={d.heatmap || Array.from({ length: 7 }, () => Array(24).fill(0))}/></Panel>
          <Panel title="Renewals ahead">
            <div className="flex h-3 rounded-full overflow-hidden gap-[2px] mb-4" aria-hidden>
              {[["expired", "#B42318"], ["d7", "#E8590C"], ["d30", "#D99A00"], ["d60", "#8CA33A"], ["later", "#1B8F5A"], ["none", "#C9BDBE"]].map(([kk, c]) => exp[kk] ? <span key={kk} style={{ width: `${(exp[kk] / expTotal) * 100}%`, background: c }}/> : null)}
            </div>
            {[["expired", "Ended — hidden from search", CircleSlash, "text-rose-700"], ["d7", "Ends within 7 days", AlertTriangle, "text-orange-700"], ["d30", "Ends in 8–30 days", Clock, "text-amber-700"], ["d60", "Ends in 31–60 days", Clock, "text-lime-700"], ["later", "More than 60 days left", CircleCheck, "text-emerald-700"], ["none", "No plan on record", Minus, "text-neutral-500"]].map(([kk, l, I, c]) => (
              <div key={kk} className="flex items-center justify-between py-2 border-b border-[color:var(--line)]/60 last:border-0 text-[14px]"><span className={`inline-flex items-center gap-2 ${c}`}><I className="w-4 h-4"/> <span className="text-[color:var(--ink)]">{l}</span></span><b className="tabular-nums">{exp[kk] || 0}</b></div>
            ))}
          </Panel>
        </div>

        <div className="grid xl:grid-cols-3 gap-6">
          <Panel title="Top cities" action={
            <div className="inline-flex p-0.5 rounded-full bg-[color:var(--cream)] border border-[color:var(--line)] text-[12px]">
              {[["demand", "Demand"], ["listings", "Listings"], ["users", "Users"]].map(([v, l]) => <button key={v} onClick={() => setCityTab(v)} className={`px-2.5 py-1 rounded-full font-semibold ${cityTab === v ? "bg-[color:var(--maroon)] text-white" : "text-[color:var(--maroon)]"}`}>{l}</button>)}
            </div>}>
            {(cityList || []).length ? cityList.map((c) => <HBar key={c.city} label={c.city} value={c.count} max={cityMax}/>) : <div className="text-neutral-500 text-[14px] py-6">Nothing yet.</div>}
          </Panel>
          <Panel title="Most viewed listings">
            {(d.top_views || []).map((v) => <HBar key={v.id} label={v.name} sub={`${v.city} · ${catLabel(v.category)}`} value={v.views} max={Math.max(1, ...(d.top_views || []).map((x) => x.views))} color={S[1]}/>)}
          </Panel>
          <Panel title="Most inquired listings">
            {(d.top_inquiries || []).length ? d.top_inquiries.map((v) => <HBar key={v.id} label={v.name || "Removed listing"} sub={v.city} value={v.count} max={Math.max(1, ...d.top_inquiries.map((x) => x.count))} color={S[2]}/>) : <div className="text-neutral-500 text-[14px] py-6">No inquiries yet.</div>}
          </Panel>
        </div>
      </>)}
    </div>
  );
}
